/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */

import {Component, Vue} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import {UgcNotificationItemInterface} from '@/store/modules/ugc-notification/types';

const namespaceNotification: string = 'ugcNotificationModule';
const namespaceBuild: string = 'ugcBuildModule';
const namespaceItem: string = 'ugcItemModule';
const namespaceUserSession: string = 'userSessionModule';

/**
 * Notification mixin.
 *
 * Provides notifications for an application.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
@Component
export default class Notifications extends Vue {
    @Action('refresh', {namespace: namespaceNotification}) public refresh: any;
    @Action('presented', {namespace: namespaceNotification}) public presented: any;
    @Getter('unactioned', {namespace: namespaceNotification}) public unactioned!: UgcNotificationItemInterface[];
    @Action('notifications', {namespace: namespaceBuild}) public revisionNotifications: any;
    @Action('notifications', {namespace: namespaceItem}) public itemNotifications: any;
    @Getter('authenticated', {namespace: namespaceUserSession}) public authenticated!: boolean;

    private items: UgcNotificationItemInterface[] = [];

    /**
     * Mounted life-cycle event.
     */
    public mounted(): void {
        this.poll();
    }

    /**
     * Poll the notification API route.
     *
     * @returns {Promise<void>}
     */
    private async poll(): Promise<void> {
        // if we are authenticated then request notifications
        if (this.authenticated) {
            await this.refresh();

            if (this.unactioned) {
                const messageableItems: UgcNotificationItemInterface[] =
                    this.unactioned.filter((unactionedItem: UgcNotificationItemInterface) => {
                        const existing: UgcNotificationItemInterface | undefined =
                            this.items.find((item: UgcNotificationItemInterface) => item.id === unactionedItem.id);
                        return undefined === existing;
                    });

                for (const messageableItem of messageableItems) {
                    this.items.push(messageableItem);
                    this.popNotification(messageableItem);
                }

                this.revisionNotifications(messageableItems);
                this.itemNotifications(messageableItems);
            }
        }

        setTimeout(() => {
            this.poll();
        }, 60000);
    }

    /**
     * Pop the notification using toastr.
     *
     * @param {UgcNotificationItemInterface} item
     */
    private popNotification(item: UgcNotificationItemInterface): void {
        const config: ToastrOptions = {
            timeOut: 6000,
            extendedTimeOut: 0,
            preventDuplicates: true,
            onShown: (): void => {
                this.presented(item);
            },
        };

        // create toastr according to status
        switch (item.status) {
            case 'failed': case 'rejected':
                this.$toastr.error(this.$i18n.translate(`notification.${item.status}`, {
                    buildName: item.itemName}) || '', '', config);
                break;
            case 'building': case 'in-queue':
                this.$toastr.info(this.$i18n.translate(`notification.${item.status}`, {
                    buildName: item.itemName}) || '', '', config);
                break;
            case 'complete':
                this.$toastr.success(this.$i18n.translate(`notification.${item.status}`, {
                    buildName: item.itemName}) || '', '', config);
                break;
        }
    }
}

